import type { TooltipProps } from '@mui/material';
import type { Company } from '@/scenes/authentication/types/types.auth';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { IconButton, Tooltip, tooltipClasses } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../theme/theme';

interface GicsItem {
  name: string;
  code: string;
}

type GicsVariant = 'large' | 'medium' | 'small';

interface GicsHierarchyProps {
  company: Company;
  className?: string;
  asTooltip?: boolean;
  variant?: GicsVariant;
}

function getFontSize(variant: GicsVariant = 'medium') {
  switch (variant) {
    case 'large':
      return '16px';
    case 'medium':
      return '14px';
    case 'small':
      return '12px';
    default:
      return '14px';
  }
}

const GicsIconWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const GicsTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.background.paper,
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(14),
    border: `1px solid ${theme.palette.divider}`,
  },
}));

const GicsLink = styled.button<{ $variant: GicsVariant }>`
  background: none;
  border: none;
  color: var(--color-brand-primary);
  text-decoration: none;
  cursor: pointer;
  font-size: ${(props) => getFontSize(props.$variant)};
  font-style: inherit;
  padding: 0;

  &:hover {
    text-decoration: underline;
    color: var(--color-darker-blue);
  }

  &:focus {
    outline: 2px solid var(--color-brand-primary);
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const GicsSeparator = styled.span<{ $variant: GicsVariant }>`
  color: var(--color-text-secondary);
  margin: 0 2px;
  font-size: ${(props) => getFontSize(props.$variant)};
`;

const GicsLinksContainer = styled.div<{ $variant: GicsVariant }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  line-height: ${(props) => getFontSize(props.$variant)}; // just use font size to keep it as tight as possible
`;

const GicsHierarchy: React.FC<GicsHierarchyProps> = ({
  company,
  className,
  asTooltip = true,
  variant = 'medium',
}: GicsHierarchyProps) => {
  const navigate = useNavigate();

  const buildGicsHierarchy = (): { name: string; code: string }[] => {
    const hierarchy: { name: string; code: string }[] = [];

    if (company.gicsSector && company.gicsCode) {
      const sectorCode = company.gicsCode.slice(0, 2);
      hierarchy.push({ name: company.gicsSector, code: sectorCode });
    }

    if (company.gicsIndustryGroup && company.gicsCode) {
      const industryGroupCode = company.gicsCode.slice(0, 4);
      hierarchy.push({ name: company.gicsIndustryGroup, code: industryGroupCode });
    }

    if (company.gicsIndustry && company.gicsCode) {
      const industryCode = company.gicsCode.slice(0, 6);
      hierarchy.push({ name: company.gicsIndustry, code: industryCode });
    }

    if (company.gicsSubIndustry && company.gicsCode) {
      const subIndustryCode = company.gicsCode.slice(0, 8);
      hierarchy.push({ name: company.gicsSubIndustry, code: subIndustryCode });
    }

    return hierarchy;
  };

  const hierarchy = buildGicsHierarchy();

  const handleGicsClick = (gicsCode: string) => {
    const searchParams = new URLSearchParams();
    searchParams.set('industry', gicsCode);
    navigate(`/company-search?${searchParams.toString()}`);
  };

  if (hierarchy.length === 0) {
    return null;
  }

  const renderGicsLinks = () => (
    <React.Fragment>
      {hierarchy.map((item, index) => (
        <span key={`gics-${item.code}`}>
          <GicsLink
            $variant={variant}
            onClick={() => handleGicsClick(item.code)}
            title={`Filter companies by ${item.name}`}
          >
            {item.name}
          </GicsLink>
          {index < hierarchy.length - 1 && <GicsSeparator $variant={variant}> &gt; </GicsSeparator>}
        </span>
      ))}
    </React.Fragment>
  );

  if (asTooltip) {
    return (
      <GicsIconWrapper className={className}>
        <GicsTooltip title={renderGicsLinks()}>
          <IconButton size="small" sx={{ color: 'var(--color-brand-primary)' }}>
            <AccountTreeIcon fontSize="small" />
          </IconButton>
        </GicsTooltip>
      </GicsIconWrapper>
    );
  }

  return (
    <GicsLinksContainer className={className} $variant={variant}>
      {renderGicsLinks()}
    </GicsLinksContainer>
  );
};

export default GicsHierarchy;
export type { GicsItem, GicsVariant };
